import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import ProductCardSkeleton from '~/components/ProductCard/ProductCardSkeleton';
import SkeletonListRows from '~/components/Skeleton/SkeletonList';
import { useGlobal } from '~/hooks/useGlobal';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import { userStore } from '~/stores/UserStore';

import ProductCard from '../../components/ProductCard/ProductCard';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { catalogStore } from '../../stores/CatalogStore';

import MobileHeaderAddress from '../Main/MobileHeader/MobileHeaderAddress';
import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../Main/MobileHeader/MobileHeaderTitle';

import EmptyFavs from './EmptyFavs';
import EmptyPurchased from './EmptyPurchased';
import EmptyWaitlist from './EmptyWaitlist';
import styles from './Favs.module.scss';

const TABS = ['products', 'previouslyOrdered', 'waitlist'] as const;

const Favs = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  const tabsTranslation: Record<(typeof TABS)[number], string> = useMemo(
    () => ({
      products: t('products'),
      recipes: t('recipes'),
      previouslyOrdered: t('previouslyOrdered'),
      waitlist: t('subscription:waitlist'),
    }),
    [],
  );

  const [tab, setTab] = useState<(typeof TABS)[number]>(TABS[0]);

  const fetchMoreElementRef = useRef<HTMLDivElement>(null);
  const { runObserver, isObservableElementVisible } = useIntersectionObserver();
  const [isLoading, setIsLoading] = useState(true);
  const [isCanFetchPurchasesMore, setIsCanFetchPurchasesMore] = useState(true);
  const [isCanFetchWaitlistMore, setIsCanFetchWaitlistMore] = useState(true);
  const [isCanFetchRecommendsMore] = useState(true);
  const [currentItems, setCurrentItems] = useState<Array<any>>([]);

  const getItemsListByTab = (activeTab: string, isForce?: boolean) => {
    switch (activeTab) {
      case TABS[0]:
        favoritesStore
          .fetchFavorites(isForce)
          .catch((error) => error && console.error(error))
          .then((result) => {
            favoritesStore.setIsCanFetchFavouritesMore(!!result);
            setCurrentItems(favoritesStore.favorites);
          })
          .finally(() => {
            setIsLoading(false);
          });
        break;
      case TABS[1]:
        setIsLoading(true);
        catalogStore
          .fetchPurchasedItems(isForce)
          .catch((error) => error && console.error(error))
          .then((isCanFetchPurchasesMore) => {
            setIsCanFetchPurchasesMore(isCanFetchPurchasesMore);
            setCurrentItems(catalogStore.purchasedItemsList);
          })
          .finally(() => {
            setIsLoading(false);
          });

        break;
      case TABS[2]:
        setIsLoading(true);
        catalogStore.notifyMeStore
          .getNotifyMeProductsList()
          .catch((error) => error && console.error(error))
          .then((isCanFetchWaitlistMore) => {
            setIsCanFetchWaitlistMore(isCanFetchWaitlistMore);
            setCurrentItems(catalogStore.notifyMeStore.notifyMeItemsList);
          })
          .finally(() => {
            setIsLoading(false);
          });

        break;
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getItemsListByTab(tab, true);

    runObserver(fetchMoreElementRef);
  }, []);

  useEffect(() => {
    if (!isObservableElementVisible) {
      return;
    }

    const shouldUpdatePurchases =
      tab === TABS[0] && favoritesStore.isCanFetchFavouritesMore;
    const shouldUpdateFavourite = tab === TABS[1] && isCanFetchPurchasesMore;
    const shouldUpdateWaitlist = tab === TABS[2] && isCanFetchWaitlistMore;

    if (
      shouldUpdatePurchases ||
      shouldUpdateFavourite ||
      shouldUpdateWaitlist
    ) {
      setIsLoading(true);
      getItemsListByTab(tab);
      return;
    }
  }, [
    isCanFetchPurchasesMore,
    isCanFetchWaitlistMore,
    isObservableElementVisible,
    favoritesStore.isCanFetchFavouritesMore,
    tab,
    isCanFetchRecommendsMore,
  ]);

  useEffect(() => {
    getItemsListByTab(tab);
  }, [tab, userStore.isAuthorized]);

  let pageContent = null;

  if (
    (isLoading || favoritesStore.favoritesStoreCRUDMeta.isLoading) &&
    currentItems.length === 0
  ) {
    pageContent = (
      <div
        data-company={company.name}
        className={classes(['block-products', styles.blockProducts])}
      >
        <SkeletonListRows rows={6}>
          <ProductCardSkeleton />
        </SkeletonListRows>
      </div>
    );
  } else if (currentItems.length > 0) {
    pageContent = (
      <div
        data-company={company.name}
        className={classes(['block-products', styles.blockProducts])}
      >
        {currentItems.map((product, i) => (
          <ProductCard
            key={`${product.id}-${i}`}
            product={product}
            className={styles.productCard}
            isRatingShown={tab !== TABS[1]}
          />
        ))}
        {favoritesStore.favoritesStoreCRUDMeta.isLoading && (
          <SkeletonListRows rows={10}>
            <ProductCardSkeleton />
          </SkeletonListRows>
        )}
      </div>
    );
  } else {
    if (tab === TABS[0]) {
      pageContent = <EmptyFavs />;
    } else if (tab === TABS[1]) {
      pageContent = <EmptyPurchased />;
    } else if (tab === TABS[2]) {
      pageContent = <EmptyWaitlist />;
    }
  }

  return (
    <div className={styles.pageFavs} data-company={company.name}>
      <MobileHeaderLayout
        content={
          company.isFavsInCabinet ? (
            <MobileHeaderTitle text={t('savedAndPurchased') || ''} />
          ) : (
            <MobileHeaderAddress />
          )
        }
        toolbarClassName={styles.toolbarContent}
      />
      <div className={classes(['content-layout favorites'])}>
        <div className={styles.tabFilters}>
          <div className={styles.tabFiltersWrapper}>
            {TABS.map((i) => (
              <Link
                key={i}
                to="#"
                className={classes([
                  styles.tabFiltersWrapperItem,
                  i === tab && styles.active,
                ])}
                onClick={() => {
                  setTab(i);
                  setCurrentItems([]);
                }}
                data-company={company.name}
              >
                {tabsTranslation[i]}
              </Link>
            ))}
          </div>
        </div>
        <div className="favs-content">
          {!company.isFavsInCabinet && (
            <nav className="breadcrumbs">
              <Link to="/" className="breadcrumbs__item">
                {t('home')}
              </Link>
              <div className="breadcrumbs__line">/</div>
              <Link to="/" className="breadcrumbs__item">
                {t('favs')}
              </Link>
            </nav>
          )}
          {isMobile && company.isFavsInCabinet ? null : (
            <h1 className={styles.title}>{tabsTranslation[tab]}</h1>
          )}
          {pageContent}
          <div ref={fetchMoreElementRef} className={styles.anchor} />
        </div>
      </div>
    </div>
  );
};

export default observer(Favs);
